import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { Visualizer } from 'react-sound-visualizer';
import { useAuth } from '../../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../config/colors';

const DeepgramRecorder = forwardRef(({ onTranscriptionUpdate, onError, onReset, onStop, layout = "vertical", helperText, additionalHelperText, priority = 'normal', transcriptSegments = [], selectedRole = '' }, ref) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(isRecording);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  const [audioStream, setAudioStream] = useState(null);
  
  const websocket = useRef(null);
  const audioContext = useRef(null);
  const mediaStreamSource = useRef(null);
  const processor = useRef(null);
  const mediaStreamRef = useRef(null);
  const texts = useRef([...transcriptSegments]);
  // After stop, CloseStream often re-sends the last final → ignore interim + dedupe finals
  const isStoppingRef = useRef(false);
  const closeTimeoutRef = useRef(null);

  // Convert Float32Array to Int16Array with dynamic amplification
  const convertFloat32ToInt16 = useCallback((float32Array) => {
    const int16Array = new Int16Array(float32Array.length);
    
    // Calculate RMS to determine signal strength
    const rms = Math.sqrt(float32Array.reduce((sum, x) => sum + x * x, 0) / float32Array.length);
    
    // Dynamic amplification: stronger when signal is weak, weaker when signal is strong
    // Target RMS of about 0.2 (experimentally determined good value)
    const targetRms = 0.2;
    const AMPLIFICATION = rms > 0.0001 ? targetRms / rms : 50;
    
    // Apply amplification with soft clipping for smoother sound
    for (let i = 0; i < float32Array.length; i++) {
      // Amplify
      const amplified = float32Array[i] * AMPLIFICATION;
      // Soft clip to avoid harsh digital clipping
      const softClipped = Math.sign(amplified) * (1 - Math.exp(-Math.abs(amplified)));
      // Convert to 16-bit integer (-32768 to 32767)
      int16Array[i] = Math.round(softClipped * 32767);
    }
    
    return int16Array;
  }, []);

  const cleanupAudioResources = useCallback(() => {
    if (processor.current) {
      processor.current.disconnect();
      processor.current = null;
    }
    if (mediaStreamSource.current) {
      mediaStreamSource.current.disconnect();
      mediaStreamSource.current = null;
    }
    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close();
      audioContext.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
      setAudioStream(null);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) {
      return;
    }

    setIsRecording(false);
    isStoppingRef.current = true;

    if (websocket.current) {
      const ws = websocket.current;
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'stop' }));
        // Brief window for Deepgram to flush a pending (non-final) utterance only
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
          closeTimeoutRef.current = null;
          ws.onmessage = null;
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
            ws.close();
          }
        }, 400);
      } else {
        ws.onmessage = null;
        ws.close();
      }
      websocket.current = null;
    }

    // Ensure a trailing slot for the next session without stacking empties
    const updatedTexts = [...texts.current];
    if (updatedTexts.length === 0 || updatedTexts[updatedTexts.length - 1] !== '') {
      updatedTexts.push('');
      texts.current = updatedTexts;
      onTranscriptionUpdate?.([...updatedTexts]);
    }

    cleanupAudioResources();
    onStop?.();
  }, [cleanupAudioResources, onStop, onTranscriptionUpdate]);

  const startRecording = useCallback(async () => {
    texts.current = [...transcriptSegments];
    isStoppingRef.current = false;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    try {
      // Request mic first while still in the user-gesture context (required by browsers)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      mediaStreamRef.current = stream;
      setAudioStream(stream);

      const myAudioContext = new AudioContext({
        sampleRate: 16000,
        latencyHint: 'interactive'
      });

      if (myAudioContext.state === 'suspended') {
        await myAudioContext.resume();
      }

      const source = myAudioContext.createMediaStreamSource(stream);
      mediaStreamSource.current = source;

      // ~64ms chunks at 16kHz
      const bufferSize = 1024;
      const scriptProcessor = myAudioContext.createScriptProcessor(bufferSize, 1, 1);
      processor.current = scriptProcessor;

      source.connect(scriptProcessor);
      scriptProcessor.connect(myAudioContext.destination);
      audioContext.current = myAudioContext;

      const token = localStorage.getItem('access_token');
      if (!token) {
        onError?.(t('audioRecorder.errors.permissions'));
        cleanupAudioResources();
        return;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsBaseUrl = process.env.REACT_APP_API_BASE_URL
        ? process.env.REACT_APP_API_BASE_URL.replace(/^https?:/, wsProtocol)
        : `${wsProtocol}//${window.location.host}`;

      const wsUrl = `${wsBaseUrl}/api/transcription/ws?token=${encodeURIComponent(token)}`;
      websocket.current = new WebSocket(wsUrl);

      websocket.current.onopen = () => {
        console.log('Deepgram Nova-3 WebSocket connected via backend, language:', user?.preferences?.language, 'role:', selectedRole);
        websocket.current.send(JSON.stringify({
          type: 'start',
          language: user?.preferences?.language || 'en',
          role: selectedRole || ''
        }));

        scriptProcessor.onaudioprocess = (e) => {
          if (websocket.current?.readyState === WebSocket.OPEN) {
            const inputData = e.inputBuffer.getChannelData(0);
            const audioData = convertFloat32ToInt16(inputData);
            // Send the Int16Array view (correct byte range), not the backing buffer
            websocket.current.send(audioData);
          }
        };

        setIsRecording(true);
      };

      websocket.current.onclose = () => {
        cleanupAudioResources();
      };

      websocket.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        onError?.(t('audioRecorder.errors.connection'));
        stopRecording();
      };

      websocket.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Nova-3 live Results
          // @see https://developers.deepgram.com/docs/live-streaming-audio
          if (data.type === 'Results') {
            const transcript = data.channel?.alternatives?.[0]?.transcript;
            if (!transcript) {
              return;
            }

            const isFinal = data.is_final;
            const updatedTexts = [...texts.current];

            if (isFinal) {
              const trimmed = transcript.trim();
              if (!trimmed) {
                return;
              }

              // Drop current interim (if any)
              if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] !== '') {
                updatedTexts.pop();
              }
              // Drop trailing empty placeholders
              while (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] === '') {
                updatedTexts.pop();
              }

              // CloseStream often re-emits the last already-committed final — skip it
              if (
                updatedTexts.length > 0 &&
                updatedTexts[updatedTexts.length - 1] === trimmed
              ) {
                updatedTexts.push('');
                texts.current = updatedTexts;
                return;
              }

              updatedTexts.push(trimmed);
              updatedTexts.push('');
              texts.current = updatedTexts;
              onTranscriptionUpdate?.([...updatedTexts]);
            } else {
              // After stop, only accept a final flush — ignore late interims
              if (isStoppingRef.current) {
                return;
              }
              if (updatedTexts.length > 0 && updatedTexts[updatedTexts.length - 1] !== '') {
                updatedTexts.pop();
              }
              updatedTexts.push(transcript);
              texts.current = updatedTexts;
              onTranscriptionUpdate?.([...updatedTexts]);
            }
          } else if (data.error) {
            console.error('Received error:', data.error);
            onError?.(data.error);
          }
        } catch (error) {
          console.error('Error handling WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('Error starting Deepgram recording:', error);
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        onError?.(t('audioRecorder.errors.permissions'));
      } else {
        onError?.(t('audioRecorder.errors.initialization'));
      }
      cleanupAudioResources();
    }
  }, [cleanupAudioResources, convertFloat32ToInt16, onError, t, user?.preferences?.language, selectedRole, onTranscriptionUpdate, transcriptSegments, stopRecording]);

  const handleToggleRecording = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x') {
        // Check if we're inside a modal (higher priority context)
        const isInModal = document.querySelector('[role="dialog"]') !== null;
        const isInFeedbackModal = document.querySelector('[data-feedback-modal]') !== null;
        
        // If this recorder has 'high' priority (feedback modal) or if no modal is open
        const shouldHandle = priority === 'high' || (!isInModal && priority === 'normal');
        
        // Additional check: if we're in a feedback modal, only high priority recorders should respond
        if (isInFeedbackModal && priority !== 'high') {
          console.log(`Deepgram Recorder (priority: ${priority}) - Skipping, feedback modal is open`);
          return;
        }
        
        if (shouldHandle) {
          console.log(`Deepgram Recorder (priority: ${priority}) - Handling shortcut`);
          event.preventDefault();
          event.stopPropagation();
          handleToggleRecording();
        } else {
          console.log(`Deepgram Recorder (priority: ${priority}) - Not handling, modal is open`);
        }
      }
    };

    // Use capture phase for high priority recorders to intercept first
    const useCapture = priority === 'high';
    window.addEventListener('keydown', handleKeyPress, useCapture);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, useCapture);
    };
  }, [handleToggleRecording, priority]);

  useEffect(() => {
    if (onReset) {
      return () => {
        if (isRecording) {
          stopRecording();
        }
      };
    }
  }, [onReset, isRecording, stopRecording]);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    stopRecording
  }));

  return (
    <div className="px-6 py-4">
      {/* Main Horizontal Controls */}
      <div className="flex items-center justify-between space-x-6 mb-4">
        {/* Left side - Audio Visualizer */}
        <div className="flex-1">
          {isRecording && audioStream ? (
            <Visualizer
              audio={audioStream}
              autoStart={true}
              strokeColor="#c5d6f8"
              mode="continuous" 
              rectWidth={3} 
              slices={50}
              heightNorm={1}
            >
              {({ canvasRef }) => (
                <canvas 
                  ref={canvasRef} 
                  style={{ height: '80px', width: '100%' }}
                  className="rounded-lg"
                />
              )}
            </Visualizer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[80px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-500">
                {isRecording ? t('audioRecorder.recording') : helperText || t('audioRecorder.clickToStart')}
                <span className="ml-2 text-xs text-gray-400">
                  {t('audioRecorder.shortcut')}
                </span>
              </p>
              {!isRecording && additionalHelperText && (
                <p className="text-xs text-gray-400 italic mt-1 px-4 text-center">
                  {additionalHelperText}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right side - Control Button */}
        <div className="flex items-center">
          {/* Record/Stop Button */}
          <button
            type="button"
            onClick={handleToggleRecording}
            className={`flex items-center justify-center w-16 h-16 rounded-full transition-all duration-200 focus:outline-none focus:ring-4 ${
              isRecording 
                ? 'bg-red-300 hover:bg-red-400 focus:ring-red-200' 
                : 'hover:bg-teal-400 focus:ring-teal-200'
            }`}
            style={!isRecording ? {
              backgroundColor: colors.primary.selected
            } : undefined}
            aria-label={isRecording ? t('audioRecorder.stopRecording') : t('audioRecorder.startRecording')}
          >
            {isRecording ? (
              <StopIcon className="w-8 h-8 text-white" />
            ) : (
              <MicrophoneIcon className="w-8 h-8 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Recording Status Bar */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
            <span className="font-medium text-red-600">{t('audioRecorder.recordingStatus')}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default DeepgramRecorder;
